import React from 'react';

import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { ModalWrapper } from 'src/legacy/components/Modals';
import type { ModalWrapperProps } from 'src/legacy/components/Modals/ModalWrapper';

interface AppLimitModalShellProps {
  /** Names the dialog for assistive tech; each route uses its own heading. */
  ariaLabel: string;
  /** Desktop width; the sheet goes full-width on mobile regardless. */
  widthPx: number;
  /**
   * Suppresses dismissal. Set while an irreversible step (a card charge) is in
   * flight, so closing cannot strand the user mid-payment.
   */
  isDismissDisabled?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

/** ModalWrapper's paper is squarer and heavier than the design's card. */
const APP_LIMIT_MODAL_CLASS = [
  '[&_.MuiDialog-paper]:rounded-lg',
  '[&_.MuiDialog-paper]:shadow-popover-050',
].join(' ');

/**
 * The chrome both app-limit paywalls sit in. The two routes differ in width and
 * in everything inside — the fee route is a compact confirm, the upgrade route a
 * plan picker — so only the dialog itself is shared.
 */
export const AppLimitModalShell: React.FC<AppLimitModalShellProps> = ({
  ariaLabel,
  widthPx,
  isDismissDisabled = false,
  onClose,
  children,
}) => {
  const isMobile = useMediaQuery('sm', 'max');

  const handleClose: ModalWrapperProps['onClose'] = () => {
    if (isDismissDisabled) return;
    onClose();
  };

  return (
    <ModalWrapper
      open
      onClose={handleClose}
      hideActions
      hideDividers
      disableGutters
      fullWidth={isMobile}
      width={isMobile ? undefined : widthPx}
      aria-label={ariaLabel}
      className={APP_LIMIT_MODAL_CLASS}
    >
      {children}
    </ModalWrapper>
  );
};
